import React, { useEffect, useRef } from 'react';

import cs from 'classnames';
import { motion } from 'framer-motion';

import useResizeObserver from 'hooks/useResizeObserver';

import {
  PARALLAX_DEFAULT_DIRECTION,
  PARALLAX_DEFAULT_PAUSE_DURATION,
  PARALLAX_DEFAULT_SPEED
} from 'constants/commons';

import styles from './ResponsiveParallaxImage.module.scss';

interface ResponsiveParallaxImageProps {
  src: string;
  speed?: number; // time in seconds for one full pass
  initialDirection?: 'up' | 'down';
  className?: string;
  pauseDuration?: number; // pause at the edge in ms
}

const ResponsiveParallaxImage: React.FC<ResponsiveParallaxImageProps> = ({
  src,
  speed = PARALLAX_DEFAULT_SPEED, // default animation duration in seconds
  initialDirection = PARALLAX_DEFAULT_DIRECTION,
  className,
  pauseDuration = PARALLAX_DEFAULT_PAUSE_DURATION
}) => {
  const { dimensions, resizeObserverRef } = useResizeObserver();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const [containerHeight, setContainerHeight] = React.useState(0);
  const [imageHeight, setImageHeight] = React.useState(0);

  // Update container height on layout or resize
  useEffect(() => {
    if (containerRef.current) {
      const { height } = containerRef.current.getBoundingClientRect();
      setContainerHeight(height);
    }
  }, [dimensions.height]);

  // Observe and update image height when it changes
  useEffect(() => {
    if (!imageRef.current) return;
    const imageNode = imageRef.current;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setImageHeight(entry.contentRect.height);
      }
    });

    observer.observe(imageNode);
    return () => observer.disconnect();
  }, []);

  // Calculate animation boundaries
  const minOffset = containerHeight - imageHeight;
  const startY = initialDirection === 'down' ? minOffset : 0;
  const endY = initialDirection === 'down' ? 0 : minOffset;

  return (
    <div
      ref={(node) => {
        containerRef.current = node;
        resizeObserverRef.current = node;
      }}
      className={cs(styles.container, className)}
    >
      <motion.img
        ref={imageRef}
        src={src}
        alt=""
        className={styles.image}
        initial={{ y: startY }} // start position based on direction
        animate={{ y: [startY, endY] }} // animate between start and end
        transition={{
          duration: speed, // animation duration for one pass
          ease: 'easeInOut', // easing function
          repeat: Infinity, // loop forever
          repeatType: 'reverse', // reverse direction after each cycle
          repeatDelay: pauseDuration / 1000 // pause at edges (converted to seconds)
        }}
      />
    </div>
  );
};

export default ResponsiveParallaxImage;
